import { useCallback, useMemo, useState } from 'react';
import type { MenuItemVariantDto } from '../../../../../../../../generated-api';
import type { CartAdditionOption, CartOrderLine, CartOrderLineComponent } from '../../../../../../../../types/cart';
import { useLocation } from '../../../../../../../context/LocationContext';
import { useCartStore } from '../../../../../../../context/cart/CartStore';
import { useCheckout } from '../../../../../../../context/CheckoutContext';
import { useCustomisations } from '../../../../../../../hooks/useCustomisations';
import { isCartAdditionOption } from '../../../../../../../utils/cart';
import { handleMissingCustomizationScroll } from '../../../../../../../utils/customizations';
import { getUniqueOrderLineKey } from '../../../../../../../utils/orderLineUtils';
import { useAllProducts } from '../../../../../../../hooks/useAllProducts';
import useIntl from '../../../../../../../hooks/useIntl';
import type { ExpandedComponent } from './types';
import { collapseComponents, expandComponents, toBaseComponent } from './types';

const getDefaultOptionsFromOrderLine = (orderLine: CartOrderLine, item: MenuItemVariantDto): CartAdditionOption[] =>
  orderLine.options
    .map((orderLineOption) => {
      const option = item.optionGroups
        .flatMap((group) => group.options)
        .find((itemOption) => itemOption.optionId === orderLineOption.itemVariantId);
      if (!option) {
        return null;
      }
      return { option, quantity: orderLineOption.quantity, type: orderLineOption.type };
    })
    .filter(isCartAdditionOption);

const getDefaultOptionsFromComponent = (
  component: CartOrderLineComponent,
  item: MenuItemVariantDto
): CartAdditionOption[] =>
  component.options
    .map((componentOption) => {
      const option = item.optionGroups
        .flatMap((group) => group.options)
        .find((itemOption) => itemOption.optionId === componentOption.itemVariantId);
      if (!option) {
        return null;
      }
      return { option, quantity: componentOption.quantity, type: componentOption.type };
    })
    .filter(isCartAdditionOption);

interface ExpandedComponentWithMenuItem {
  component: ExpandedComponent;
  item: MenuItemVariantDto;
}

/** Recursively collect all expanded components with their matching menu items */
const collectAllComponents = (
  components: ExpandedComponent[],
  allProducts: MenuItemVariantDto[]
): ExpandedComponentWithMenuItem[] => {
  const result: ExpandedComponentWithMenuItem[] = [];

  for (const component of components) {
    const item = allProducts.find((p) => p.variantLinkId === component.itemVariantId);
    if (!item) {
      continue;
    }

    result.push({ component, item });

    if (component.components && component.components.length > 0) {
      result.push(...collectAllComponents(component.components, allProducts));
    }
  }

  return result;
};

/** Check if a specific component has all required options satisfied */
const isComponentValid = (item: MenuItemVariantDto, options: CartAdditionOption[]): boolean =>
  item.optionGroups
    .filter((og) => og.minSelection > 0)
    .every((og) => {
      const selectedCount = options
        .filter((opt) => og.options.some((groupOpt) => groupOpt.optionId === opt.option.optionId))
        .reduce((sum, opt) => sum + opt.quantity, 0);
      return selectedCount >= og.minSelection;
    });

/** Build the save-ready components tree from expanded state, stripping instanceId */
const buildSaveComponents = (
  expanded: ExpandedComponent[],
  optionsMap: Map<string, CartAdditionOption[]>,
  commentsMap: Map<string, string>
): CartOrderLineComponent[] =>
  expanded.map((ec) => {
    const options = optionsMap.get(ec.instanceId) || [];
    const comment = commentsMap.get(ec.instanceId) || undefined;
    const children = ec.components as ExpandedComponent[] | undefined;
    return toBaseComponent(ec, {
      comment,
      commentId: comment ? ec.commentId || crypto.randomUUID() : undefined,
      components: children ? buildSaveComponents(children, optionsMap, commentsMap) : undefined,
      options: options.map((o) => ({
        itemVariantId: o.option.optionId,
        name: o.option.name,
        price: o.option.price,
        takeawayPrice: o.option.takeawayPrice,
        quantity: o.quantity,
        type: o.type,
      })),
    });
  });

export const useCompositeEditState = (parentOrderLine: CartOrderLine, parentItem: MenuItemVariantDto) => {
  const { formatCurrency } = useLocation();
  const updateCompositeProduct = useCartStore((x) => x.updateCompositeProduct);
  const intl = useIntl();
  const { vatType } = useCheckout();

  const allProducts = useAllProducts();

  /** Expanded components with instanceIds */
  const expandedRootComponents = useMemo(
    () => expandComponents(parentOrderLine.components ?? []),
    [parentOrderLine.components]
  );

  /** All expanded components at all levels, flattened with their menu items */
  const allComponentsWithItems = useMemo(
    () => collectAllComponents(expandedRootComponents, allProducts),
    [expandedRootComponents, allProducts]
  );

  // Parent state
  const [parentOptions, setParentOptions] = useState<CartAdditionOption[]>(
    getDefaultOptionsFromOrderLine(parentOrderLine, parentItem)
  );
  const [parentComment, setParentComment] = useState(parentOrderLine.comment || '');

  // Component state - flat maps keyed by instanceId
  const [componentOptionsMap, setComponentOptionsMap] = useState<Map<string, CartAdditionOption[]>>(
    () =>
      new Map(
        allComponentsWithItems.map(({ component, item }) => [
          component.instanceId,
          getDefaultOptionsFromComponent(component, item),
        ])
      )
  );

  const [componentCommentsMap, setComponentCommentsMap] = useState<Map<string, string>>(
    () => new Map(allComponentsWithItems.map(({ component }) => [component.instanceId, component.comment || '']))
  );

  // Parent validation
  const {
    hasAddedAllRequiredOptionGroups: parentValid,
    requiredOptionGroups,
    addedRequiredOptionGroups,
  } = useCustomisations(parentItem, parentOptions);

  // Component validation - check every component at every level
  const allComponentsValid = allComponentsWithItems.every(({ item, component }) => {
    const options = componentOptionsMap.get(component.instanceId) || [];
    return isComponentValid(item, options);
  });

  /** Check if a single component (by instanceId) has valid options */
  const isComponentValidById = useCallback(
    (instanceId: string): boolean => {
      const entry = allComponentsWithItems.find((c) => c.component.instanceId === instanceId);
      if (!entry) {
        return true;
      }
      const options = componentOptionsMap.get(instanceId) || [];
      return isComponentValid(entry.item, options);
    },
    [allComponentsWithItems, componentOptionsMap]
  );

  /** Check if a composite component and all its descendants are valid */
  const isSubtreeValid = useCallback(
    (component: ExpandedComponent): boolean => {
      if (!isComponentValidById(component.instanceId)) {
        return false;
      }
      const children = component.components as ExpandedComponent[] | undefined;
      if (!children) {
        return true;
      }
      return children.every((child) => isSubtreeValid(child));
    },
    [isComponentValidById]
  );

  const isValid = parentValid && allComponentsValid;

  // Update helpers
  const updateComponentOptions = useCallback(
    (
      instanceId: string,
      optionsOrFn: CartAdditionOption[] | ((prev: CartAdditionOption[]) => CartAdditionOption[])
    ) => {
      setComponentOptionsMap((prev) => {
        const currentOptions = prev.get(instanceId) || [];
        const newOptions = typeof optionsOrFn === 'function' ? optionsOrFn(currentOptions) : optionsOrFn;
        const updated = new Map(prev);
        updated.set(instanceId, newOptions);
        return updated;
      });
    },
    []
  );

  const updateComponentComment = useCallback((instanceId: string, comment: string) => {
    setComponentCommentsMap((prev) => {
      const updated = new Map(prev);
      updated.set(instanceId, comment);
      return updated;
    });
  }, []);

  // Price calculation
  const totalPrice = useMemo(() => {
    const parentBasePrice =
      vatType === 'TAKEAWAY' && parentItem.takeawayPrice ? parentItem.takeawayPrice : parentItem.price;
    let total = parentBasePrice * parentOrderLine.quantity;

    parentOptions.forEach((option) => {
      const optionPrice =
        vatType === 'TAKEAWAY' && option.option.takeawayPrice ? option.option.takeawayPrice : option.option.price;
      total += optionPrice * option.quantity * parentOrderLine.quantity;
    });

    componentOptionsMap.forEach((options) => {
      options.forEach((option) => {
        const optionPrice =
          vatType === 'TAKEAWAY' && option.option.takeawayPrice ? option.option.takeawayPrice : option.option.price;
        total += optionPrice * option.quantity * parentOrderLine.quantity;
      });
    });

    return total;
  }, [vatType, parentItem, parentOptions, componentOptionsMap, parentOrderLine.quantity]);

  const actionLabel = `${intl.formatMessage({ id: 'generic.save' })} (${formatCurrency(totalPrice)})`;

  // Save handler
  const handleSave = useCallback(() => {
    if (!isValid) {
      if (!parentValid) {
        handleMissingCustomizationScroll(requiredOptionGroups, addedRequiredOptionGroups);
      }
      return;
    }

    updateCompositeProduct({
      componentUpdates: [],
      components: collapseComponents(
        buildSaveComponents(expandedRootComponents, componentOptionsMap, componentCommentsMap)
      ),
      orderLineKey: getUniqueOrderLineKey(parentOrderLine),
      parentComment,
      parentOptions: parentOptions.map((option) => ({
        itemVariantId: option.option.optionId,
        name: option.option.name,
        price: option.option.price,
        takeawayPrice: option.option.takeawayPrice,
        quantity: option.quantity,
        type: option.type,
      })),
      quantity: parentOrderLine.quantity,
    });
  }, [
    isValid,
    parentValid,
    requiredOptionGroups,
    addedRequiredOptionGroups,
    updateCompositeProduct,
    parentOrderLine,
    parentOptions,
    parentComment,
    expandedRootComponents,
    componentOptionsMap,
    componentCommentsMap,
  ]);

  /** Resolve the MenuItemVariantDto for a given component */
  const getMenuItemForComponent = useCallback(
    (itemVariantId: string): MenuItemVariantDto | undefined =>
      allProducts.find((p) => p.variantLinkId === itemVariantId),
    [allProducts]
  );

  return {
    // Parent state
    parentOptions,
    setParentOptions,
    parentComment,
    setParentComment,
    // Component state
    expandedRootComponents,
    componentOptionsMap,
    componentCommentsMap,
    updateComponentOptions,
    updateComponentComment,

    // Validation
    isValid,
    isSubtreeValid,

    // Actions
    handleSave,
    actionLabel,

    // Lookups
    getMenuItemForComponent,
    parentItem,
  };
};
